export const ce = (type, attributes = {}, children = []) => {
  const special = ['classes', 'dataset', 'listeners'];
  const el = document.createElement(type);

  if (attributes.classes) {
    el.classList.add(...attributes.classes);
  }

  if (attributes.dataset) {
    Object.entries(attributes.dataset).forEach(([key, value]) => (el.dataset[key] = value));
  }

  if (attributes.listeners) {
    Object.entries(attributes.listeners).forEach(([eventName, handler]) => el.addEventListener(eventName, handler));
  }

  Object.entries(attributes)
    .filter(([prop]) => !special.includes(prop))
    .forEach(([prop, value]) => (el[prop] = value));

  children.forEach(child => {
    if (typeof child === 'string') {
      el.innerText += child;
    } else {
      el.appendChild(child);
    }
  });

  return el;
};

/**
 * @description sets body gender theme
 * @param theme
 * @returns void
 */
export const setGenderTheme = theme => {
  if (!document.body || !document.body.classList) {
    return;
  }

  if (theme && window.location.pathname !== '/') {
    document.body.classList.remove('men-theme', 'woman-theme');
    try {
      document.body.classList.add(theme);
    } catch (e) {
      document.body.classList.add('woman-theme');
    }
  }
};

/**
 * @description checks if number is in range including start/end
 * @param val
 * @returns {boolean}
 */
export const inRange = (x, xmin, xmax) => x >= xmin && x <= xmax;

/**
 * @description Is null
 * @param val
 * @returns {boolean}
 */
export const isNull = val => val === null;

/**
 * @description Is array
 * @param val
 * @returns {boolean}
 */
export const isArr = val => Array.isArray(val);

/**
 * @description Is object
 * @param val
 * @returns {boolean}
 */
export const isObj = val => typeof val === 'object' && !isArr(val) && !isNull(val);

/**
 * @description Is number
 * @param val
 * @returns {boolean}
 */
export const isNum = val => typeof val === 'number' && !Number.isNaN(val);

/**
 * @description Is function
 * @param val
 * @returns {boolean}
 */
export const isFunc = val => typeof val === 'function';

/**
 * @description Is string
 * @param val
 * @returns {boolean}
 */
export const isStr = val => typeof val === 'string';

/**
 * @description Is undefined
 * @param val
 * @returns {boolean}
 */
export const isUndef = val => typeof val === 'undefined';

/**
 * @description Is boolean
 * @param val
 * @returns {boolean}
 */
export const isBool = val => typeof val === 'boolean';

/**
 * @description To int
 * @param val
 * @returns {number}
 */
export const toInt = val => parseInt(val, 10);

/**
 * @description Is non empty string
 * @param val
 * @returns {boolean|boolean}
 */
export const isNonEmptyStr = val => isStr(val) && val !== '';

/**
 * @description Is non empty array
 * @param val
 * @returns {boolean|boolean}
 */
export const isNonEmptyArr = val => isArr(val) && val.length > 0;

export const replaceValuesInCurlyBraces = (string, values) => {
  let value = [];

  string.split(' ').forEach(element => {
    if (element.includes('{') && element.includes('}')) {
      value.push(element.replace(/\{[^\}]+\}/g, `${values.pop()}`));
    } else {
      value.push(element);
    }
  });
  return value.join(' ');
};

/**
 * @description returns bool if the string value is true
 * @param string value
 * @returns {boolean|boolean}
 */
export const isTrueSet = value => value === 'true';

/**
 * @description returns bool if the string value is matches the given regex expression
 * @param value string that we want to compare
 * @param regexExp valid regex expression
 * @returns {boolean|boolean}
 */
export const matchesRegex = (value, regexExp) => regexExp.test(String(value).toLowerCase());

/**
 * @description returns numbes as count of symbols after decimal
 * @param any which is converted to string
 * @returns {number}
 */
export const getAfterDecimalCount = value => {
  let symbols = String(value).split('.');
  if (symbols.length > 1) {
    return symbols[1].length;
  } else {
    return 0;
  }
};

/**
 * @description returns true if the element is in the viewport plus the offset if given
 * @param documentElement to look for
 * @param offset not required set to 1 if no param given
 * @returns {boolean}
 */

export const isInViewport = (element, offset = 1) => {
  const { top, bottom } = element.getBoundingClientRect();
  return top + offset >= 0 && bottom - offset <= (window.innerHeight || document.documentElement.clientHeight);
};

/**
 * @description returns random number in inclusive range at both the minimum and the maximum
 * @param min number start
 * @param max number stop
 * @returns {number}
 */

export const getRandomIntInclusive = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const isVisible = elem => {
  if (!elem) return false;
  const styles = window.getComputedStyle(elem);
  return !(styles.display === 'none' || styles.visibility === 'hidden');
};

/**
 * @description returns true if the document matches the media query string
 * @param string representing a media query
 * @returns {boolean}
 */

export const isMatchingMedia = mediaQuery => window.matchMedia(mediaQuery).matches;

export function debounce(func, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

/**
 * @description returns an index (number) by extracting it from a specific attribute via ReGex
 * @param string HtmlElement & html attribute name (string)
 * @returns {Number}
 */

export function getIndexFromAttribute(element, attributeName) {
  const regex = /\d+/;
  const attribute = element.getAttribute(attributeName);
  const index = Number(attribute.match(regex)[0]);
  return index;
}

export function initializeAddClassMethod() {
  if (!Element.prototype.hasOwnProperty('addClass')) {
    Element.prototype.addClass = function (className) {
      if (!this.classList.contains(className)) {
        this.classList.add(className);
      }
    };
  }
}

/**
 * Checks if 24 hours have passed since the given date.
 *
 * @function hasExpired
 * @param {string} date - The date to compare in ISO 8601 format (e.g., "2023-07-04T14:11:00.097Z").
 * @returns {boolean} Returns true if 24 hours have passed since the given date, or false otherwise.
 */
export const hasExpired = (date, maxHours = 24) => {
  const currentDate = new Date();
  const givenDate = new Date(date);
  const elapsedMilliseconds = currentDate - givenDate;
  const elapsedHours = elapsedMilliseconds / (1000 * 60 * 60); // Convert milliseconds to hours

  return elapsedHours >= maxHours;
};
